import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';

import { organizationPlansPath } from 'application/types/routes';
import { ApplicationParams } from 'types/route_params';

import { showGleap } from 'services/gleap';
import { useBilling } from 'hooks/api/useBilling';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useHasAccess } from 'hooks/useHasAccess';

import { Button } from 'components/Button/Button';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { Text } from 'components/Text/Text';

import styles from './SuspensionBanner.module.scss';

const c = classNames.bind(styles);

export function SuspensionBanner() {
  const { applicationID } = useParams<ApplicationParams>();
  const { data: organization } = useCurrentOrganization();
  const [hasAccess] = useHasAccess();
  const { mutate: manageBilling, isLoading: isOpeningBilling } = useBilling();

  if (!organization?.is_suspended || !applicationID) {
    return null;
  }

  const isBillingUser = hasAccess('billing');
  const plansPath = organizationPlansPath(applicationID, organization.id);

  const message =
    organization.suspension_reason === 'trial_end'
      ? isBillingUser
        ? 'Your trial has ended. Select a plan to restore access.'
        : 'Your trial has ended. Please ask a billing administrator to select a plan to restore access.'
      : organization.suspension_reason === 'payment'
        ? 'Your payment did not succeed. Update billing to restore access.'
        : 'You will not be able to create new deployments or upload new files. Please contact support to resolve your account status.';

  return (
    <section id="suspension-banner" className={c('banner', 'theme warning')}>
      <Icon name="warning" />

      <div className="banner-text">
        <Heading level="4">
          Your organization {organization.name} has been temporarily suspended.
        </Heading>
        <Text>{message}</Text>
      </div>

      <div className={c('actions')}>
        {isBillingUser && organization.suspension_reason === 'trial_end' && (
          <Button size="small" variant="secondary" to={plansPath}>
            View plans
          </Button>
        )}

        {isBillingUser && organization.suspension_reason === 'payment' && (
          <Button
            size="small"
            variant="secondary"
            onClick={() => manageBilling()}
            loading={isOpeningBilling}
          >
            Update payment info
          </Button>
        )}

        <Button size="small" variant="secondary" onClick={showGleap}>
          Contact support
        </Button>
      </div>
    </section>
  );
}
